import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Divider, Button, Chip, Paper,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import ShareIcon from '@mui/icons-material/Share';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GavelIcon from '@mui/icons-material/Gavel';
import CookieIcon from '@mui/icons-material/Cookie';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const C = {
  purple:     '#552583',
  purpleDark: '#33154f',
  gold:       '#FDB927',
  bg:         '#E4F0F6',
  white:      '#ffffff',
  text:       '#1a1a1a',
  textSoft:   '#4a4a4a',
  textMuted:  '#7a7a7a',
};

const sections = [
  { id: 'responsable',    label: 'Responsable',     icon: <PersonIcon      sx={{ fontSize: 18 }} /> },
  { id: 'collecte',       label: 'Données',          icon: <StorageIcon     sx={{ fontSize: 18 }} /> },
  { id: 'utilisation',    label: 'Utilisation',      icon: <SecurityIcon    sx={{ fontSize: 18 }} /> },
  { id: 'partage',        label: 'Partage',          icon: <ShareIcon       sx={{ fontSize: 18 }} /> },
  { id: 'conservation',   label: 'Conservation',     icon: <AccessTimeIcon  sx={{ fontSize: 18 }} /> },
  { id: 'droits',         label: 'Vos droits',       icon: <GavelIcon       sx={{ fontSize: 18 }} /> },
  { id: 'cookies',        label: 'Cookies',          icon: <CookieIcon      sx={{ fontSize: 18 }} /> },
  { id: 'securite',       label: 'Sécurité',         icon: <LockIcon        sx={{ fontSize: 18 }} /> },
  { id: 'modifications',  label: 'Modifications',    icon: <EditIcon        sx={{ fontSize: 18 }} /> },
  { id: 'contact',        label: 'Contact',          icon: <ContactMailIcon sx={{ fontSize: 18 }} /> },
];

const sectionContent = [
  {
    id: 'responsable',
    title: '1. Responsable du traitement',
    icon: <PersonIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Identité',
        text: 'La société Usafiri, plateforme multi-services opérant à Mayotte (976), est responsable du traitement de vos données personnelles au sens du Règlement Général sur la Protection des Données (RGPD – Règlement UE 2016/679) et de la loi Informatique et Libertés.',
      },
      {
        subtitle: 'Coordonnées',
        text: 'Email : contact@usafiri.yt\nAdresse : Mayotte, France (976)\nSite web : usafiri.yt',
      },
    ],
  },
  {
    id: 'collecte',
    title: '2. Données collectées',
    icon: <StorageIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Données d\'identification',
        text: 'Nom, prénom, adresse e-mail, numéro de téléphone, date de naissance. Ces données sont collectées lors de la création de votre compte Usafiri.',
      },
      {
        subtitle: 'Données de navigation',
        text: 'Adresse IP, type de navigateur, pages visitées, durée de session, données de localisation (avec votre consentement) pour les services de covoiturage et VTC.',
      },
      {
        subtitle: 'Données de transaction',
        text: 'Historique des commandes, réservations, paiements (nous ne stockons jamais vos coordonnées bancaires complètes — les paiements sont traités par Stripe).',
      },
      {
        subtitle: 'Données tierces (Google)',
        text: 'Si vous vous connectez via Google, nous recevons votre nom, adresse e-mail et photo de profil depuis votre compte Google, conformément aux autorisations que vous accordez.',
      },
    ],
  },
  {
    id: 'utilisation',
    title: '3. Utilisation des données',
    icon: <SecurityIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Fourniture des services',
        text: 'Vos données nous permettent de créer et gérer votre compte, traiter vos commandes et réservations, assurer le service de livraison, covoiturage, VTC et location, ainsi que vous envoyer les confirmations et mises à jour de vos commandes.',
      },
      {
        subtitle: 'Amélioration de la plateforme',
        text: 'Analyse des usages pour améliorer nos services, détection et prévention des fraudes, statistiques anonymes sur l\'utilisation de la plateforme.',
      },
      {
        subtitle: 'Communication',
        text: 'Envoi d\'e-mails transactionnels (confirmation, vérification, réinitialisation de mot de passe). Notifications importantes liées à votre compte ou à vos réservations. Avec votre consentement : offres promotionnelles et actualités Usafiri.',
      },
    ],
  },
  {
    id: 'partage',
    title: '4. Partage des données',
    icon: <ShareIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Partenaires de service',
        text: 'Certaines de vos données (nom, numéro de téléphone) peuvent être partagées avec nos restaurateurs, conducteurs ou prestataires partenaires uniquement dans le cadre de l\'exécution de votre commande ou réservation.',
      },
      {
        subtitle: 'Prestataires techniques',
        text: 'Google Cloud (hébergement), Stripe (paiement), SendGrid (e-mails). Ces prestataires sont soumis à des engagements contractuels de confidentialité stricts et n\'utilisent pas vos données à leurs propres fins.',
      },
      {
        subtitle: 'Obligations légales',
        text: 'Nous pouvons divulguer vos données aux autorités compétentes si la loi l\'exige ou en cas de procédure judiciaire. Nous ne vendons jamais vos données personnelles à des tiers.',
      },
    ],
  },
  {
    id: 'conservation',
    title: '5. Durée de conservation',
    icon: <AccessTimeIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Données de compte',
        text: 'Conservées pendant toute la durée de votre relation avec Usafiri, puis supprimées dans un délai de 3 ans après la clôture de votre compte ou votre dernière connexion.',
      },
      {
        subtitle: 'Données de transaction',
        text: 'Conservées 10 ans à compter de la date de la transaction, conformément aux obligations légales comptables et fiscales françaises.',
      },
      {
        subtitle: 'Données de navigation et cookies',
        text: 'Données de navigation conservées 13 mois maximum. Tokens de session : 30 jours. Tokens de vérification e-mail : 15 minutes.',
      },
    ],
  },
  {
    id: 'droits',
    title: '6. Vos droits',
    icon: <GavelIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Droit d\'accès',
        text: 'Vous pouvez demander à tout moment à consulter l\'ensemble des données personnelles que nous détenons sur vous.',
      },
      {
        subtitle: 'Droit de rectification',
        text: 'Vous pouvez corriger ou mettre à jour vos données directement depuis votre espace « Profil » ou en nous contactant.',
      },
      {
        subtitle: 'Droit à l\'effacement',
        text: 'Vous pouvez demander la suppression de vos données personnelles (droit à l\'oubli), sous réserve de nos obligations légales de conservation.',
      },
      {
        subtitle: 'Droit à la portabilité',
        text: 'Vous pouvez demander à recevoir vos données dans un format structuré et lisible par machine.',
      },
      {
        subtitle: 'Droit d\'opposition',
        text: 'Vous pouvez vous opposer à tout moment au traitement de vos données à des fins de prospection commerciale.',
      },
      {
        subtitle: 'Exercer vos droits',
        text: 'Pour exercer l\'un de ces droits, contactez-nous à contact@usafiri.yt. Vous avez également le droit d\'introduire une réclamation auprès de la CNIL (www.cnil.fr).',
      },
    ],
  },
  {
    id: 'cookies',
    title: '7. Cookies et traceurs',
    icon: <CookieIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Cookies essentiels',
        text: 'Nécessaires au fonctionnement de la plateforme : maintien de votre session, sécurité du compte, préférences de base. Ces cookies ne peuvent pas être désactivés.',
      },
      {
        subtitle: 'Cookies de performance',
        text: 'Nous utilisons Google reCAPTCHA Enterprise pour protéger nos formulaires contre les robots. Ces cookies collectent des données anonymes sur les interactions avec le site.',
      },
      {
        subtitle: 'Gestion des cookies',
        text: 'Vous pouvez configurer votre navigateur pour refuser tout ou partie des cookies. Cela peut toutefois affecter certaines fonctionnalités de la plateforme. Nous ne déposons aucun cookie publicitaire ou de suivi tiers sans votre consentement.',
      },
    ],
  },
  {
    id: 'securite',
    title: '8. Sécurité des données',
    icon: <LockIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Mesures techniques',
        text: 'Chiffrement HTTPS (TLS) de toutes les communications. Mots de passe hashés avec bcrypt. Authentification à deux facteurs (2FA) par e-mail disponible. Tokens JWT sécurisés pour les sessions.',
      },
      {
        subtitle: 'Infrastructure',
        text: 'Hébergement sur Google Cloud Platform (région europe-west1), base de données Cloud SQL avec accès restreint, sauvegardes automatiques chiffrées, contrôle d\'accès strict à la production.',
      },
      {
        subtitle: 'En cas de violation',
        text: 'En cas de violation de données susceptible d\'engendrer un risque pour vos droits, nous nous engageons à vous en informer dans les 72 heures conformément au RGPD.',
      },
    ],
  },
  {
    id: 'modifications',
    title: '9. Modifications de la politique',
    icon: <EditIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Mises à jour',
        text: 'Nous pouvons mettre à jour cette politique de confidentialité à tout moment pour refléter les évolutions de nos services ou les exigences légales. La date de dernière mise à jour est indiquée en haut de cette page.',
      },
      {
        subtitle: 'Notification',
        text: 'En cas de modification substantielle, vous serez informé par e-mail ou par une notification visible sur la plateforme avant l\'entrée en vigueur des nouvelles dispositions.',
      },
    ],
  },
  {
    id: 'contact',
    title: '10. Contact et réclamations',
    icon: <ContactMailIcon sx={{ fontSize: 28, color: C.purple }} />,
    content: [
      {
        subtitle: 'Délégué à la protection des données',
        text: 'Pour toute question relative à cette politique ou à vos données personnelles, contactez notre équipe à : contact@usafiri.yt',
      },
      {
        subtitle: 'Autorité de contrôle',
        text: 'Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la Commission Nationale de l\'Informatique et des Libertés (CNIL) :\nwww.cnil.fr — 3, Place de Fontenoy, 75007 Paris.',
      },
    ],
  },
];

export default function PolitiqueConfidentialite() {
  const navigate = useNavigate();
  const [active, setActive] = useState('responsable');

  const scrollTo = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ background: C.bg, minHeight: '100vh' }}>

      {/* Hero */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.purpleDark} 0%, ${C.purple} 60%, #7a3aad 100%)`,
        color: C.white,
        py: { xs: 6, md: 10 },
        px: 2,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Décors */}
        <Box sx={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(253,185,39,0.08)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '20px',
            background: 'rgba(253,185,39,0.15)',
            border: '1.5px solid rgba(253,185,39,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <VerifiedUserIcon sx={{ fontSize: 38, color: C.gold }} />
          </Box>
        </Box>

        <Typography variant="h3" fontWeight={800} sx={{ mb: 1.5, fontSize: { xs: '1.8rem', md: '2.6rem' }, letterSpacing: '-0.5px' }}>
          Politique de Confidentialité
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.75, mb: 3, maxWidth: 500, mx: 'auto' }}>
          Comment Usafiri collecte, utilise et protège vos données personnelles
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip label="Mise à jour : mai 2025" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }} />
          <Chip label="Conforme RGPD" sx={{ bgcolor: 'rgba(253,185,39,0.18)', color: C.gold, fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(253,185,39,0.3)' }} />
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>

        {/* Navigation rapide */}
        <Paper elevation={0} sx={{
          mb: 5, p: 2.5, borderRadius: '16px',
          background: C.white,
          border: '1px solid rgba(85,37,131,0.1)',
          boxShadow: '0 4px 20px rgba(85,37,131,0.06)',
        }}>
          <Typography variant="body2" fontWeight={700} sx={{ color: C.purple, mb: 1.5, fontSize: '0.7rem', letterSpacing: '0.08em' }}>
            NAVIGATION RAPIDE
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sections.map((s) => (
              <Chip
                key={s.id}
                icon={s.icon}
                label={s.label}
                onClick={() => scrollTo(s.id)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  borderRadius: '10px',
                  bgcolor: active === s.id ? C.purple : 'rgba(85,37,131,0.06)',
                  color: active === s.id ? C.white : C.purple,
                  border: active === s.id ? 'none' : '1px solid rgba(85,37,131,0.12)',
                  '& .MuiChip-icon': { color: active === s.id ? C.gold : C.purple },
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: active === s.id ? C.purpleDark : 'rgba(85,37,131,0.12)' },
                }}
              />
            ))}
          </Box>
        </Paper>

        {/* Sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sectionContent.map((section, idx) => (
            <Paper
              key={section.id}
              id={section.id}
              elevation={0}
              sx={{
                borderRadius: '20px',
                background: C.white,
                border: '1px solid rgba(85,37,131,0.08)',
                boxShadow: '0 4px 24px rgba(85,37,131,0.06)',
                overflow: 'hidden',
                scrollMarginTop: '80px',
              }}
            >
              {/* En-tête de section */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                p: { xs: 2.5, md: 3.5 },
                background: 'linear-gradient(135deg, rgba(85,37,131,0.04) 0%, rgba(253,185,39,0.04) 100%)',
                borderBottom: '1px solid rgba(85,37,131,0.08)',
              }}>
                <Box sx={{
                  width: 52, height: 52, borderRadius: '14px', flexShrink: 0,
                  background: 'rgba(85,37,131,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {section.icon}
                </Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: C.text, fontSize: { xs: '1rem', md: '1.15rem' } }}>
                  {section.title}
                </Typography>
              </Box>

              {/* Contenu */}
              <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                {section.content.map((block, i) => (
                  <Box key={i}>
                    {i > 0 && <Divider sx={{ my: 2.5, borderColor: 'rgba(85,37,131,0.07)' }} />}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ width: 4, borderRadius: 4, flexShrink: 0, background: `linear-gradient(180deg, ${C.purple}, ${C.gold})`, minHeight: 24 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: C.purple, mb: 0.8, fontSize: '0.88rem' }}>
                          {block.subtitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: C.textSoft, lineHeight: 1.8, whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                          {block.text}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Footer de la page */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: C.purple, borderColor: 'rgba(85,37,131,0.25)', border: '1.5px solid',
              borderRadius: '12px', px: 3, py: 1.2, fontWeight: 700, textTransform: 'none',
              '&:hover': { background: 'rgba(85,37,131,0.06)', borderColor: C.purple },
            }}
          >
            Retour
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 3, color: C.textMuted }}>
            © {new Date().getFullYear()} Usafiri — Tous droits réservés · Mayotte (976)
          </Typography>
        </Box>

      </Container>
    </Box>
  );
}
